import type { AppConfig } from '@ai-tutor/types';

// Application configuration
export const appConfig: AppConfig = {
  appName: import.meta.env.VITE_APP_NAME || 'AI Tutor',
  version: import.meta.env.VITE_APP_VERSION || '0.1.0',
  apiBaseUrl: import.meta.env.VITE_API_URL || 'http://localhost:8000',
  environment:
    (import.meta.env.VITE_APP_ENVIRONMENT as AppConfig['environment']) ||
    'development',
  features: {
    // Feature flags for conditional functionality
    darkMode: true,
    notifications: true,
    analytics: import.meta.env.VITE_ENABLE_ANALYTICS === 'true',
    debugging: import.meta.env.VITE_ENABLE_DEV_TOOLS === 'true',
    experimental: false,

    // Specific feature flags
    chatbot: true,
    voiceInput: false,
    offlineMode: false,
    // Video generation + doubt mode. Doubt mode is a sub-feature of video
    // (it needs a completed video), so this single flag governs both. Off by
    // default; enabled in local dev via VITE_ENABLE_VIDEO=true.
    video: import.meta.env.VITE_ENABLE_VIDEO === 'true',
    multiLanguage: false,
    accessibility: true,

    // UI features
    animations: true,
    soundEffects: false,
    tooltips: true,
    keyboardShortcuts: true,
  },
};

// Environment-specific configurations
export const environmentConfigs = {
  development: {
    ...appConfig,
    features: {
      ...appConfig.features,
      debugging: true,
      experimental: true,
    },
  },
  staging: {
    ...appConfig,
    features: {
      ...appConfig.features,
      debugging: false,
      experimental: true,
      analytics: true,
    },
  },
  production: {
    ...appConfig,
    features: {
      ...appConfig.features,
      debugging: false,
      experimental: false,
      analytics: true,
    },
  },
};

// Get configuration for current environment
export const getCurrentConfig = (): AppConfig => {
  return (
    environmentConfigs[
      appConfig.environment as keyof typeof environmentConfigs
    ] || appConfig
  );
};

// Feature flag utilities
export const isFeatureEnabled = (
  featureName: keyof AppConfig['features']
): boolean => {
  const config = getCurrentConfig();
  return Boolean(config.features[featureName]);
};
